import type { TFunction } from 'i18next';
import { Download, LoaderCircle } from 'lucide-react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import type { RowAction } from '@/components/ui/row-actions-menu';
import { monthRange } from '../list/list-query';
import {
  EXPENSES_MANAGE,
  EXPENSES_REPORT_READ,
  holds,
  useExpensesPermissions,
} from '../use-expenses-permissions';
import {
  downloadExpensesExport,
  downloadNotesExport,
  type ExportClass,
  type ExportOutcome,
} from './api';

const CLASSES: readonly string[] = ['A1', 'A2', 'P', 'PG', 'PD', 'none'];

/** Gestione and the Revisore download the exports (design §6); the server checks again. */
export function useMayExport(): boolean {
  const permissions = useExpensesPermissions();
  return holds(permissions, EXPENSES_MANAGE) || holds(permissions, EXPENSES_REPORT_READ);
}

function ExportButton({
  label,
  scope,
  run,
}: {
  readonly label: string;
  /** What the file holds (#952 M5), as the button's description and tooltip. */
  readonly scope: string;
  readonly run: () => Promise<ExportOutcome>;
}) {
  const { t } = useTranslation('expenses');
  const scopeId = useId();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const click = async () => {
    setBusy(true);
    setFailed(false);
    const outcome = await run();
    setBusy(false);
    setFailed(!outcome.ok);
  };
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <span id={scopeId} className="sr-only">
        {scope}
      </span>
      <Button
        type="button"
        variant="outline"
        disabled={busy}
        aria-busy={busy}
        title={scope}
        aria-describedby={scopeId}
        onClick={() => void click()}
      >
        {busy ? <LoaderCircle className="animate-spin" /> : <Download />}
        {label}
      </Button>
      {failed ? (
        <span role="alert" className="text-destructive text-sm">
          {t('exports.failed')}
        </span>
      ) : null}
    </span>
  );
}

/** The same export as a «···» menu item (the phone): a failure is said in a toast. */
function exportAction(
  id: string,
  label: string,
  failed: string,
  run: () => Promise<ExportOutcome>,
): RowAction {
  return {
    id,
    label,
    icon: Download,
    onSelect: () => {
      void run().then((outcome) => {
        if (!outcome.ok) toast.error(failed);
      });
    },
  };
}

/** `YYYY-MM` to the year the notes export is asked for. */
function yearOf(period: string | null): number | undefined {
  return period === null ? undefined : Number(period.slice(0, 4));
}

function runNotes(year: number | undefined, owner: string | null) {
  return downloadNotesExport({
    ...(year === undefined ? {} : { year }),
    ...(owner === null ? {} : { owner: [owner] }),
  });
}

function runExpenses(period: string | null, owner: string | null, internalClass: string | null) {
  const range = period === null ? null : monthRange(period);
  const chosen =
    internalClass !== null && CLASSES.includes(internalClass)
      ? (internalClass as ExportClass)
      : undefined;
  return downloadExpensesExport({
    ...(chosen === undefined ? {} : { class: chosen }),
    ...(range === null ? {} : range),
    ...(owner === null ? {} : { owner: [owner] }),
  });
}

/** What the notes file holds: the year of the list's month, or every year. */
function notesScope(t: TFunction<'expenses'>, year: number | undefined): string {
  return year === undefined ? t('exports.notesAllYears') : t('exports.notesYear', { year });
}

/**
 * The notes file in nexpense's format, for the phone's «···» menu (decision 223: «Esporta formato
 * nexpense»); empty for someone who may not export.
 */
export function useNotesExportActions(period: string | null, owner: string | null) {
  const { t } = useTranslation('expenses');
  const may = useMayExport();
  if (!may) return [];
  const year = yearOf(period);
  return [
    exportAction('export-notes', t('exports.nexpense'), t('exports.failed'), () =>
      runNotes(year, owner),
    ),
  ];
}

/**
 * The expenses file in nexpense's format, for the phone's «···» menu; empty for someone who may
 * not export.
 */
export function useExpensesExportActions(
  period: string | null,
  owner: string | null,
  internalClass: string | null,
) {
  const { t } = useTranslation('expenses');
  const may = useMayExport();
  if (!may) return [];
  return [
    exportAction('export-expenses', t('exports.nexpense'), t('exports.failed'), () =>
      runExpenses(period, owner, internalClass),
    ),
  ];
}

/**
 * «Esporta formato nexpense» on the notes list (decision 223): the year of the list's month, or
 * every year when it shows all periods; the list's owner when one is chosen.
 */
export function ExportNotesButton({
  period,
  owner,
}: {
  /** `YYYY-MM`, or null for every period. */
  readonly period: string | null;
  readonly owner: string | null;
}) {
  const { t } = useTranslation('expenses');
  if (!useMayExport()) return null;
  const year = yearOf(period);
  return (
    <ExportButton
      label={t('exports.nexpense')}
      scope={notesScope(t, year)}
      run={() => runNotes(year, owner)}
    />
  );
}

/** «Esporta formato nexpense» on the expenses list: the list's month, class and owner, by class. */
export function ExportExpensesButton({
  period,
  owner,
  internalClass,
}: {
  readonly period: string | null;
  readonly owner: string | null;
  readonly internalClass: string | null;
}) {
  const { t } = useTranslation('expenses');
  if (!useMayExport()) return null;
  return (
    <ExportButton
      label={t('exports.nexpense')}
      scope={t('exports.expenses')}
      run={() => runExpenses(period, owner, internalClass)}
    />
  );
}
